import {FederatedPointerEvent, Graphics, Polygon} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {FrmVar} from "../../drawfrm/FrmVar";
import {inches, lineBlueGrey, lineBlueHeavy, lineBlueLight, lineRed} from "../../const";
import {FrameBlockInnerDimension, InnerDimensionTarget} from "./FrameBlockInnerDimension";
import {drawCutMarker} from "../../shared-2d/cutHatch";
import {Label} from "../../shared-2d/Label";
import {crossThickness, isStraight, slopeAt, STRAIGHT, StickSlope} from "./Slope";
import {addAnchors, anchorTarget} from "./Anchors";
import type {OutlineSide} from "./BlockOps";
import {MdFrameStick, sticksById} from "../../model/frame/MdFrameBlock";
import {drawDashedPolygon} from "../../shared-2d/dashed";
import {MIN_AIR_GAP} from "./Util";

// text height (scene units) of the "Cutdown: {dim}" callout, and the gap below the
// jamb's bottom edge where it sits.
const CUTDOWN_LABEL_FONT = 30;
const CUTDOWN_LABEL_GAP = 2 * inches;

// the cut-plan mark drawn on a stick: a circle of CUT_LABEL_RADIUS around the letter.
const CUT_LABEL_FONT = 60;
const CUT_LABEL_RADIUS = 2.5 * inches;

export type StickOrientation = "vertical" | "horizontal";

// the minimal surface StickMaterial needs from its owning view: the sibling sticks
// (for snap alignment while resizing), the shared container the snap guide draws
// into, and a re-solve hook. FrameGridView implements it; an inert host (e.g. the
// Locations scene) can supply trivial versions since inert sticks never resize.
export interface StickHost {
    elev: ElevationApp;
    sticks: StickMaterial[];
    parentThing: DrawFrmThing;
    render(): void;
}

// how a stick's end is cut:
//   - cap:   a flat end square to the stick (both end corners at the same
//            position along the length)
//   - miter: an angled end for a corner joint. Sticks are assumed to meet at
//            90deg, so the cut runs from one end corner to the inner corner of
//            the otherThickness x thickness corner box. `direction` is which end
//            corner (left/right) is the inner corner that stays at the stick's
//            nominal end; the opposite (outer) corner extends past the end by
//            the meeting stick's thickness, adding the miter onto the length.
//            The angle falls out of this thickness vs. the meeting stick's
//            thickness (45deg when equal).
//   - extend: a square end that runs past the nominal end by the meeting stick's
//             thickness on *both* end corners, so this stick owns the corner and
//             the meeting stick butts (caps) against it.
// `corners` sets each end corner's outset past the nominal end outright, for an end
// that meets a sloped stick (see Terminations): the rules above then don't apply.
export type StickTermination =
    | {kind: "cap"; corners?: CornerOutsets}
    | {kind: "miter"; otherThickness: FrmVar; direction: "left" | "right"; corners?: CornerOutsets}
    | {kind: "extend"; otherThickness: FrmVar; corners?: CornerOutsets};

export type CornerOutsets = {left: number; right: number};

const CAP: StickTermination = {kind: "cap"};

// how close (in screen pixels) the dragged stick's centre-line must come to
// another same-orientation stick's centre-line before it snaps into alignment.
// Screen-based so the feel is the same at any zoom.
const SNAP_PX = 12;

// on-screen thickness of the snap guide line, in pixels.
const SNAP_GUIDE_PX = 1.5;

// a same-orientation stick reduced to the coordinates the snap needs: its
// centre-line along the drag (perpendicular) axis, plus its extent along its
// own length so the guide can span between the two aligned sticks.
export type SnapGeo = {center: number; alongMin: number; alongMax: number};

// drag-to-resize behaviour for a stick, supplied by the owner. An interior
// mullion reallocates flow space between the two blocks it divides (see
// makeMullionResize); a perimeter edge grows/shrinks the edge cell it abuts,
// expanding the overall frame (see makePerimeterResize).
export interface StickResize {
    // capture the neighbours' current sizes as the baseline the drag offsets from.
    begin(): void;
    // reallocate `delta` scene units (perpendicular to the stick; positive = drag
    // toward the trailing/high-coordinate block) from the trailing block to the
    // leading one, then re-solve the grid. Called on every pointer move. Return a
    // handoff when the drag must go on as a drag of another stick (see StickHandoff).
    move(delta: number): StickHandoff | void;
    // release the drag (nothing to persist beyond the last move).
    end(): void;
}

// a drag that goes on as a drag of another stick: e.g. the first step drag of a
// perimeter piece creates the step stick, which then takes the drag over. `commit`
// makes the change (which may rebuild the grid) and returns the id of the stick that
// continues the drag, or null to end it.
export type StickHandoff = {commit: () => string | null};

// StickMaterial is a single piece of frame stock (a jamb or a head). It's a
// rectangle (or trapezoid, once mitred) whose cross-section thickness and length
// are driven by the FrmVars passed in by the owner (OuterFrame).
//
// It exposes a reference point at each of the stick's four corners so an owner
// can link sticks corner-to-corner. A/B are the two ends along the stick's
// length; Left/Right are the two corners across its thickness:
//   - vertical:   A = top end,  B = bottom end (Left = min x, Right = max x)
//   - horizontal: A = left end, B = right end  (Left = min y, Right = max y)
const FILL_DEFAULT = lineBlueGrey;
const FILL_HOVER = lineBlueLight;
const FILL_SELECTED = lineBlueLight;

// an air gap draws as a dashed outline with no fill until it is hovered or selected.
const AIR_GAP_LINE_ALPHA = 0.6;

// the sticks that are frame stock: an air gap is laid out like a stick but is open
// space, so the joinery and the cut plan leave it out.
export function stockSticks(sticks: StickMaterial[]): StickMaterial[] {
    return sticks.filter(s => !s.airGap);
}

export class StickMaterial extends DrawFrmThing implements InnerDimensionTarget {

    private elev: ElevationApp;
    view: StickHost;
    orientation: StickOrientation;
    length: FrmVar;
    thickness: FrmVar;
    private terminationA: StickTermination;
    private terminationB: StickTermination;

    // frame-undercut amount cut off this stick's B (bottom) end, or null when the
    // stick has no undercut. Only meaningful for a vertical jamb, and kept within the
    // stick's footprint so the frame layout is untouched. How it's drawn depends on
    // undercutHatch: the bottom `undercut` band as a red hatch (Locations view), or a
    // plain red line at the bottom edge (every other view).
    private undercut: FrmVar | null;
    private undercutHatch: boolean;

    // when true, this jamb draws the "Cutdown: {dim}" callout (printout only). Set on a
    // single jamb so the export shows one callout, not one per undercut stick.
    private showCutdown: boolean;

    // "Cutdown: {dim}" callout drawn under the jamb on the printout, so the shortened
    // jamb reads its cut amount. Created lazily the first time it's needed; hidden otherwise.
    private undercutLabel: Label | null = null;

    // the cut-plan mark (a letter in a circle) drawn at this stick's centre on the cut-plan
    // printout, or null when it has none. See setCutLabel.
    private cutLabel: {bubble: Graphics; label: Label} | null = null;

    // dimension showing just the stick's thickness (the cross-section). The other
    // axis (length) is set by the layout, so there's nothing to dimension there.
    // Null for an inert stick (interactive: false), which is purely visual.
    private thicknessDimension: FrameBlockInnerDimension | null = null;

    private hovered = false;

    // drag-to-resize behaviour, or null for a stick that isn't resizable (e.g. a
    // perimeter edge abutting a door). Supplied by the owner.
    private resize: StickResize | null;

    // reallocates a thickness edit into the cells either side, or null when the
    // owner doesn't want the edit absorbed (perimeter sticks, which grow the frame
    // instead). Supplied by the owner.
    private absorbThickness: ((delta: number) => void) | null;

    // resolves the centre-line of the block this stick divides, as an extra snap
    // target for the drag. Null for a stick with no such centre to offer.
    private centerSnap: (() => SnapGeo | null) | null;

    // the active resize drag, or null. Captured at pointer-down: `startGlobalPerp`
    // is the pointer's screen position along the resize (perpendicular-to-stick)
    // axis and `scale` the screen-pixels-per-scene-unit there. Working the delta in
    // screen space keeps it immune to the layout reflow the drag itself triggers
    // (the stick's own graphic shifts as neighbours re-flow). Bound handlers are
    // stored so they can be detached on end / destroy.
    // startCenter / along are this stick's own centre-line and length-extent at
    // drag start (deltaScene is measured from the same start, so snapping stays
    // baseline-consistent). targets is a snapshot of every other same-orientation
    // stick's centre-line + extent, taken once at grab time.
    private gesture: {
        startGlobalPerp: number;
        scale: number;
        startCenter: number;
        along: {min: number; max: number};
        targets: SnapGeo[];
    } | null = null;
    private onGestureMove = (e: FederatedPointerEvent) => this.gestureMove(e);
    private onGestureEnd = () => this.endGesture();

    // the snap alignment guide, drawn in the shared stick coordinate space
    // (parentThing) while a snap is active. Created lazily on first snap, cleared
    // when no snap is active, and destroyed on drag end.
    private snapGuide: Graphics | null = null;

    // the selection id of this piece. The first piece of a stick uses the model
    // stick's id; a further piece the renderer split off adds "#n" (see pieceStickId).
    id: string;

    // the id of the model stick this piece draws.
    stickId: string;

    // the model stick this piece draws, or null for a stick with no model (an inert
    // stick). Read live: its air-gap flag can flip without the piece being rebuilt.
    private model: MdFrameStick | null;

    // true when this piece draws an air gap (see MdFrameStick.airGap).
    get airGap(): boolean {
        return !!this.model?.airGap;
    }

    // which of this piece's ends are the model stick's own ends. A stick the renderer
    // draws in several pieces (beside a step) shares one model stick, so only the first
    // piece's A end and the last piece's B end carry its terminateA/B override.
    modelEnds = {A: true, B: true};

    // the side of this piece a void lies on, for a piece of an interior stick drawn beside a
    // void. null otherwise. Set by the owner each render.
    voidSide: OutlineSide | null = null;

    // the slope of this piece (see Slope.ts): the band runs from the A end to the B end
    // along it, inside the slot the layout gave the stick. Set by the owner each render.
    slope: StickSlope = STRAIGHT;

    constructor(elev: ElevationApp, opt: {
        id: string;
        // the model stick's id when `id` is a split-off piece's own id. Defaults to `id`.
        stickId?: string;
        // the model stick this piece draws. Omit for a stick with no model.
        model?: MdFrameStick;
        view: StickHost;
        orientation: StickOrientation;
        length: FrmVar;
        thickness: FrmVar;
        terminationA?: StickTermination;
        terminationB?: StickTermination;
        // drag-to-resize behaviour; omit for perimeter sticks (not resizable).
        resize?: StickResize;
        // reallocate a thickness edit into the neighbouring cells; omit for a stick
        // whose thickness should grow the frame rather than eat into a cell.
        absorbThickness?: (delta: number) => void;
        // the centre-line of the block this stick divides, offered as an extra snap
        // target while it's dragged. Resolved at drag start; return null to offer
        // none. Omit for a stick that can't be centred in anything.
        centerSnap?: () => SnapGeo | null;
        // when false, the stick is purely visual: no hover/select/resize, and no
        // thickness dimension. Used by the Locations scene for its frame jambs.
        // Defaults to true.
        interactive?: boolean;
        // frame-undercut amount removed from this stick's B (bottom) end. Omit for
        // sticks that aren't undercut.
        undercut?: FrmVar;
        // when true, the undercut is drawn as a red hatch band (Locations view);
        // otherwise as a plain red line at the bottom edge. Defaults to false.
        undercutHatch?: boolean;
        // when true, the plain-line undercut also gets a "Cutdown: {dim}" callout on
        // the printout. Set on a single jamb so the export shows one callout. Defaults to false.
        showCutdown?: boolean;
    }) {
        super();

        this.elev = elev;
        this.view = opt.view;
        this.id = opt.id;
        this.stickId = opt.stickId ?? opt.id;
        this.model = opt.model ?? null;
        this.orientation = opt.orientation;
        this.length = opt.length;
        this.thickness = opt.thickness;
        this.resize = opt.resize ?? null;
        this.absorbThickness = opt.absorbThickness ?? null;
        this.centerSnap = opt.centerSnap ?? null;
        this.terminationA = opt.terminationA ?? CAP;
        this.terminationB = opt.terminationB ?? CAP;
        this.undercut = opt.undercut ?? null;
        this.undercutHatch = opt.undercutHatch ?? false;
        this.showCutdown = opt.showCutdown ?? false;

        const interactive = opt.interactive ?? true;

        // the thickness is the cross-section axis: it runs along x for a vertical
        // stick and along y for a horizontal one. Draw only that axis. Skipped
        // entirely for an inert stick, which carries no dimension.
        if(interactive) {
            this.thicknessDimension = new FrameBlockInnerDimension(this, {
                axes: this.orientation === "vertical" ? {width: true, height: false} : {width: false, height: true},
            });
            this.track(this.thicknessDimension);
        }

        const onChange = () => {
            this.draw();
        };

        // geometry depends on our own length/thickness and the meeting sticks'
        // thicknesses (for mitred ends)
        this.trackSubscription(this.length.subscribe(onChange));
        this.trackSubscription(this.thickness.subscribe(onChange));
        if(this.undercut) this.trackSubscription(this.undercut.subscribe(onChange));
        for(const term of [this.terminationA, this.terminationB]) {
            if(term.kind === "miter" || term.kind === "extend") {
                this.trackSubscription(term.otherThickness.subscribe(onChange));
            }
        }
        this.draw();

        // an inert stick skips all interactivity: no selection highlight, no
        // dimension toggle, no hover/click, no resize. It just draws.
        if(!interactive) {
            return;
        }

        // selecting this stick on the canvas drives the contextual "Selected Stick"
        // ribbon tab (insert / remove live there now). Keep our own highlight in
        // sync with that selection, and clear it when another element is selected.
        this.trackSubscription(this.elev.viewSettings.selection.subscribeAndFireLast(() => {
            this.setSelected(this.elev.viewSettings.isSelected(this.id) || this.anchorsSelected());
            this.syncThicknessDimensionVisible();
        }));

        this.syncThicknessDimensionVisible();

        // the "Show All Dimensions" view toggle drives whether the thickness
        // dimension is drawn; redraw when it flips.
        this.trackSubscription(this.elev.viewSettings.dimShowAll.subscribe(() => {
            this.syncThicknessDimensionVisible();
            this.draw();
        }));

        // hover recolors the fill; click selects the stick (and shows its tab).
        this.makeInteractive({
            hitArea: () => {
                const c = this.corners();
                return new Polygon([
                    c.aLeft.x, c.aLeft.y,
                    c.bLeft.x, c.bLeft.y,
                    c.bRight.x, c.bRight.y,
                    c.aRight.x, c.aRight.y,
                ]);
            },
            onHover: (hovered, e) => {
                // in anchor setup a stick that can't take anchors doesn't react.
                const setup = !!this.elev.viewSettings.anchorSetup.value;
                const target = setup ? anchorTarget(this.elev.frameSpec, this.stickId, this.voidSide) : null;
                this.lines.cursor = this.hoverCursor(setup, target);
                this.hovered = hovered && (!setup || !!target);
                this.draw();
            },
            onSelectedChange: () => this.draw(),
            onClick: (e) => this.onTap(e),
        });

        // a resizable stick is draggable perpendicular to its length — an interior
        // mullion moves the boundary between the two blocks it divides, a perimeter
        // edge grows the frame — recomputing the whole grid live as it drags. A
        // plain tap (no drag) still selects via makeInteractive's pointertap above.
        // The cursor hints the drag axis: a vertical stick moves left/right, a
        // horizontal one up/down. A stick with no resize handler stays select-only.
        if(this.resize && !this.elev.readOnly) {
            this.lines.cursor = this.orientation === "vertical" ? "ew-resize" : "ns-resize";
            this.lines.on("pointerdown", (e: FederatedPointerEvent) => this.beginResize(e));
        }
    }

    // a tap either selects the stick (single click) or opens its thickness editor
    // (double click). Pixi sets `detail` to the consecutive-click count on the
    // pointertap, so detail >= 2 is a double-click.
    private onTap(e: FederatedPointerEvent) {
        if(this.elev.viewSettings.anchorSetup.value) {
            this.tapAnchors();
            return;
        }

        // while the ribbon waits for a stick pick (e.g. Split By), a tap answers it
        // instead of changing the selection.
        const pick = this.elev.viewSettings.stickPick.value;
        if(pick) {
            pick.onPick(this.stickId);
            return;
        }

        if(e.detail >= 2 && !this.elev.readOnly) {
            this.editThickness();
            return;
        }
        this.select(e);
    }

    // the cursor over this stick: in anchor setup, "+" on a stick that can take anchors and
    // has none, else the stick's normal cursor.
    private hoverCursor(setup: boolean, target: string | null): string {
        if(!setup) {
            if(!this.resize || this.elev.readOnly) return "pointer";
            return this.orientation === "vertical" ? "ew-resize" : "ns-resize";
        }

        if(!target) return "default";
        return sticksById(this.elev.frameSpec.frameBlocks).get(target)?.anchors ? "pointer" : "cell";
    }

    // anchor setup: a tap on a stick with no anchors gives it one section of the paint
    // product; a tap on a stick with anchors selects them.
    private tapAnchors() {
        const vs = this.elev.viewSettings;
        const spec = this.elev.frameSpec;
        const target = anchorTarget(spec, this.stickId, this.voidSide);
        if(!target) {
            vs.clearSelection();
            // the tap lit this stick up; put its highlight back in line with the selection.
            this.setSelected(this.anchorsSelected());
            return;
        }

        if(!sticksById(spec.frameBlocks).get(target)?.anchors) {
            // view only: a stick with no anchors has nothing to show.
            if(this.elev.readOnly) {
                vs.clearSelection();
                this.setSelected(false);
                return;
            }

            const paint = vs.anchorSetup.value?.paintProduct ?? null;
            if(paint === null) {
                this.setSelected(this.anchorsSelected());
                this.elev.draw.showError("Pick an anchor product first");
                return;
            }

            addAnchors(spec, target, paint);
            spec.frameBlocksChanged.emit(false);
        }

        vs.selectItem({type: "anchor-stick", id: "anchor:" + target, stickId: target}, false);
    }

    // true in anchor setup when this stick's anchors (or one of their sections) are selected.
    private anchorsSelected(): boolean {
        const vs = this.elev.viewSettings;
        if(!vs.anchorSetup.value) return false;

        const anchorSel = vs.selection.value.filter(s => s.type === "anchor-stick" || s.type === "anchor-section");
        if(anchorSel.length === 0) return false;

        const target = anchorTarget(this.elev.frameSpec, this.stickId, this.voidSide);
        return anchorSel.some(s => (s.type === "anchor-stick" || s.type === "anchor-section") && s.stickId === target);
    }

    // open the editor for the stick's thickness dimension, the same popover a tap
    // on its number opens. The dimension is drawn on whichever axis carries the
    // thickness (see the constructor), and the preceding single-click selected the
    // stick, which is what makes it visible.
    private editThickness() {
        this.thicknessDimension?.openEditor(this.orientation === "vertical" ? "width" : "height");
    }

    // drive the contextual "Selected Stick" ribbon tab, and highlight this stick.
    // ctrl/meta-click adds to (or toggles within) the current stick selection; a
    // plain click (or a resize-drag start, which passes no event) selects just this
    // one.
    private select(e?: FederatedPointerEvent) {
        console.log("stick selected:", this.id);
        const vs = this.elev.viewSettings;
        const additive = !!e && (e.ctrlKey || e.metaKey);
        vs.selectItem({type: "stick", id: this.id, orientation: this.orientation}, additive);
        vs.activeTab.emit("selected");
    }

    // ---- drag to move the boundary between neighbouring blocks ---------------

    // begin a resize drag. Grabs the pointer (suspending the viewport pan and
    // listening globally) so a move anywhere continues the drag even once the
    // pointer leaves the thin stick.
    private beginResize(e: FederatedPointerEvent) {
        if(this.destroyed || !this.resize) return;
        e.stopPropagation();

        // ctrl/meta-click is a (multi-)select, not a drag: bail before starting the
        // gesture (and before the non-additive select below) so the pointertap
        // handler's additive select can extend the selection instead of replacing it.
        if(e.ctrlKey || e.metaKey) return;

        // a stick pick and anchor setup are answered by the tap (see onTap), so they start
        // no drag.
        if(this.elev.viewSettings.stickPick.value || this.elev.viewSettings.anchorSetup.value) return;

        this.select();
        this.startGesture(this.orientation === "vertical" ? e.global.x : e.global.y);
    }

    // take over a drag another stick handed off (see StickHandoff), from the pointer's
    // current position along the drag axis. `scale` is the handing-off drag's scale:
    // a stick the rebuild just made has no world transform of its own until the next
    // frame. False when this stick can't drag.
    continueGesture(globalPerp: number, scale: number): boolean {
        if(this.destroyed || !this.resize) return false;
        this.startGesture(globalPerp, scale);
        return true;
    }

    // start tracking a drag from `globalPerp` (the pointer's screen position along the
    // drag axis): snapshot the snap targets, begin the resize and listen globally.
    private startGesture(globalPerp: number, scale?: number) {
        if(!this.resize) return;

        // world scale along the drag axis, to turn a screen-pixel delta back into
        // scene units. No scene rotation here, so the transform's a/d are the scale.
        if(scale === undefined) {
            const wt = this.graphic.worldTransform;
            scale = (this.orientation === "vertical" ? wt.a : wt.d) || 1;
        }

        // snapshot our own centre-line and the centre-lines of every other
        // same-orientation stick so a move can snap into alignment with them.
        const self = this.snapGeo();
        const targets = this.view.sticks
            .filter(s => s !== this && s.orientation === this.orientation)
            .map(s => s.snapGeo());

        // plus the centre of the block this stick divides, so a stick with nothing
        // to line up against can still be dropped exactly on centre.
        const center = this.centerSnap?.();
        if(center) targets.push(center);

        this.gesture = {
            startGlobalPerp: globalPerp,
            scale,
            startCenter: self.center,
            along: {min: self.alongMin, max: self.alongMax},
            targets,
        };
        this.elev.dragging = true;
        this.elev.draw.layoutFrozen = true;
        this.resize.begin();

        const draw = this.elev.draw;
        draw.setPanSuspended(true);
        draw.pixi.stage.eventMode = "static";
        draw.pixi.stage.on("globalpointermove", this.onGestureMove);
        window.addEventListener("pointerup", this.onGestureEnd);
        window.addEventListener("pointercancel", this.onGestureEnd);
    }

    private gestureMove(e: FederatedPointerEvent) {
        const g = this.gesture;
        if(!g || this.destroyed || !this.resize) return;

        // screen-space delta along the perpendicular axis, converted to scene units.
        // Positive = drag toward the trailing (right / bottom) block.
        const globalPerp = this.orientation === "vertical" ? e.global.x : e.global.y;
        let deltaScene = (globalPerp - g.startGlobalPerp) / g.scale;

        // snap: if the resulting centre-line lands within SNAP_PX (screen) of
        // another same-orientation stick's centre-line, pull it exactly onto it.
        const predicted = g.startCenter + deltaScene;
        const tol = SNAP_PX / g.scale;
        let snap: SnapGeo | null = null;
        let best = tol;
        for(const t of g.targets) {
            const d = Math.abs(t.center - predicted);
            if(d <= best) { best = d; snap = t; }
        }
        if(snap) {
            deltaScene = snap.center - g.startCenter;
            this.drawSnapGuide(snap, g.along, g.scale);
        } else {
            this.clearSnapGuide();
        }

        // hand the delta to the owner, which reallocates that space between the two
        // blocks and re-solves the whole grid live.
        const handoff = this.resize.move(deltaScene);
        if(handoff) this.handOff(handoff, globalPerp);
    }

    // stop this drag without ending the resize, make the handoff's change, and let the
    // stick it names go on with the drag from here. The drag stays on through the
    // change: the release-time rules (see normalizeSteps) wait for it, and the drawing
    // stays put under the pointer.
    private handOff(handoff: StickHandoff, globalPerp: number) {
        const view = this.view;
        const elev = this.elev;
        const scale = this.gesture?.scale ?? 1;
        this.detachGesture(true);

        const id = handoff.commit();
        const next = id ? view.sticks.find(s => s.id === id && !s.destroyed) : undefined;
        if(next?.continueGesture(globalPerp, scale)) return;

        // no stick goes on: the drag is over, so release it here.
        elev.dragging = false;
        elev.draw.layoutFrozen = false;
        view.render();
    }

    private endGesture() {
        if(!this.gesture) return;
        this.detachGesture(false);
        this.resize?.end();
    }

    // stop listening for the drag and give the viewport its pan back. With
    // `handingOff`, the drag goes on with another stick, so it stays flagged as on.
    private detachGesture(handingOff: boolean) {
        this.gesture = null;
        if(!handingOff) {
            this.elev.dragging = false;
            this.elev.draw.layoutFrozen = false;
        }
        this.destroySnapGuide();

        const draw = this.elev.draw;
        draw.pixi.stage.off("globalpointermove", this.onGestureMove);
        window.removeEventListener("pointerup", this.onGestureEnd);
        window.removeEventListener("pointercancel", this.onGestureEnd);
        draw.setPanSuspended(false);
    }

    destroy() {
        this.endGesture();
        super.destroy();
    }

    // ---- InnerDimensionTarget ----------------------------------------------
    // map the stick's thickness/length onto x/y extents so the dimension geometry
    // lands correctly: a vertical stick is `thickness` wide and `length` tall; a
    // horizontal one is `length` wide and `thickness` tall. The dimension is then
    // restricted (in the constructor) to whichever axis carries the thickness.
    get width(): FrmVar {
        return this.orientation === "vertical" ? this.thickness : this.length;
    }

    get height(): FrmVar {
        return this.orientation === "vertical" ? this.length : this.thickness;
    }

    get dimensionContainer() {
        return this.graphic;
    }

    // editing the thickness changes flow sums (interior mullions) and corner
    // miters, so re-run the whole grid layout.
    relayout() {
        this.view.render();
    }

    // hand a thickness edit to the owner's absorb so the neighbouring cells give up
    // the space before the layout re-solves. Only the thickness axis is dimensioned
    // (see the constructor), so the other axis never reaches here. An air gap refuses
    // a gap under MIN_AIR_GAP.
    onDimensionEdit(axis: "width" | "height", delta: number): boolean | void {
        const thicknessAxis = this.orientation === "vertical" ? "width" : "height";
        if(axis !== thicknessAxis) return;
        if(this.airGap && this.thickness.get() < MIN_AIR_GAP) return false;
        this.absorbThickness?.(delta);
    }

    // change this piece's slope (redraws when it changed).
    setSlope(slope: StickSlope) {
        if(this.slope.a === slope.a && this.slope.b === slope.b) return;
        this.slope = slope;
        this.draw();
    }

    hasSlope(): boolean {
        return !isStraight(this.slope);
    }

    // the band's extent across the length axis: the stock thickness, more on a slope.
    crossThickness(): number {
        return crossThickness(this.thickness.get(), this.length.get(), this.slope);
    }

    // the band's Left and Right faces at `along` (local cross coords; past the ends the
    // slope goes on).
    facesAt(along: number): {left: number; right: number} {
        const left = slopeAt(this.slope, this.length.get(), along);
        return {left, right: left + this.crossThickness()};
    }

    // how far a given end corner extends past the nominal end (along the length)
    // because of its miter; 0 for a cap or for the inner corner the miter leaves
    // at the nominal end. An end that meets a sloped stick sets each corner outright.
    private outset(term: StickTermination, corner: "left" | "right"): number {
        // an air gap has no ends to cut: it spans exactly its slot.
        if(this.airGap) return 0;
        if(term.corners) return term.corners[corner];
        if(term.kind === "miter" && term.direction !== corner) {
            return term.otherThickness.get();
        }
        // extend pushes both end corners past the nominal end so the stick reaches
        // across the corner box and the meeting stick butts against its side.
        if(term.kind === "extend") {
            return term.otherThickness.get();
        }
        return 0;
    }

    // where this stick landed in scene coords (set by the owner during layout).
    // Used to detect cross intersections by aligning collinear sticks.
    get positionX(): number {
        return this.graphic.position.x;
    }

    get positionY(): number {
        return this.graphic.position.y;
    }

    // this stick's centre-line along its cross (thickness) axis plus its extent
    // along its length, in the shared stick coordinate space. Used to align
    // centre-lines while dragging and to span the snap guide between two sticks.
    private snapGeo(): SnapGeo {
        const len = this.length.get();
        const t = this.thickness.get();
        if(this.orientation === "vertical") {
            return {center: this.positionX + t / 2, alongMin: this.positionY, alongMax: this.positionY + len};
        }
        return {center: this.positionY + t / 2, alongMin: this.positionX, alongMax: this.positionX + len};
    }

    // draw the alignment guide: a thin line on the snapped centre-line, spanning
    // from the further end of this stick to the further end of the stick it's
    // aligning with, so the two read as being on the same line. Screen-constant
    // width via the drag's world scale.
    private drawSnapGuide(target: SnapGeo, along: {min: number; max: number}, scale: number) {
        if(!this.snapGuide) {
            this.snapGuide = new Graphics();
            this.view.parentThing.lines.addChild(this.snapGuide);
        }

        const min = Math.min(along.min, target.alongMin);
        const max = Math.max(along.max, target.alongMax);

        const g = this.snapGuide;
        g.clear();
        g.lineStyle(SNAP_GUIDE_PX / scale, lineBlueLight, 0.9);
        if(this.orientation === "vertical") {
            g.moveTo(target.center, min);
            g.lineTo(target.center, max);
        } else {
            g.moveTo(min, target.center);
            g.lineTo(max, target.center);
        }
    }

    private clearSnapGuide() {
        this.snapGuide?.clear();
    }

    private destroySnapGuide() {
        if(this.snapGuide && !this.snapGuide.destroyed) this.snapGuide.destroy();
        this.snapGuide = null;
    }

    // how the given end is cut (see StickTermination).
    termination(end: "A" | "B"): StickTermination {
        return end === "A" ? this.terminationA : this.terminationB;
    }

    // change the frame-undercut amount cut off this stick's B end (null = none).
    setUndercut(undercut: FrmVar | null) {
        if(this.undercut === undercut) return;
        this.undercut = undercut;
        this.draw();
    }

    // the frame undercut cut off this stick's B end, or 0 when it has none.
    undercutAmount(): number {
        if(!this.undercut || this.orientation !== "vertical") return 0;
        return Math.min(this.undercut.get(), this.length.get());
    }

    // show `text` as the cut-plan mark at this stick's centre.
    setCutLabel(text: string) {
        if(!this.cutLabel) {
            // the bubble goes in before the label so the letter draws on top of it.
            const bubble = new Graphics();
            this.lines.addChild(bubble);
            const label = new Label({text, fontSize: CUT_LABEL_FONT});
            this.add(label);
            this.cutLabel = {bubble, label};
        }

        this.cutLabel.label.setText(text);
        this.drawCutLabel();
    }

    private drawCutLabel() {
        if(!this.cutLabel) return;

        const len = this.length.get();
        const faces = this.facesAt(len / 2);
        const cross = (faces.left + faces.right) / 2;
        const x = this.orientation === "vertical" ? cross : len / 2;
        const y = this.orientation === "vertical" ? len / 2 : cross;

        const g = this.cutLabel.bubble;
        g.clear();
        g.lineStyle(3, 0x000000, 1);
        g.beginFill(0xffffff, 1);
        g.drawCircle(x, y, CUT_LABEL_RADIUS);
        g.endFill();

        this.cutLabel.label.graphic.position.set(x, y);
    }

    // update one end's termination after construction. The termination resolver
    // runs each render (once positions are known), so it drives the cut here:
    // recompute the corner ref points, redraw, and re-place anything linked to us.
    setTermination(end: "A" | "B", term: StickTermination) {
        if(end === "A") this.terminationA = term;
        else this.terminationB = term;

        this.draw();
    }

    // set both ends' cuts, redrawing only when one of them changed.
    setTerminations(a: StickTermination, b: StickTermination) {
        if(sameTermination(this.terminationA, a) && sameTermination(this.terminationB, b)) return;

        this.terminationA = a;
        this.terminationB = b;
        this.draw();
    }

    // the four corners in local coords. The nominal length spans the inner edge
    // (A end at length 0, B end at the far length); a mitred corner's outer
    // corner extends past the end by the meeting stick's thickness, so the miter
    // is added onto the length. Left/Right is the cross-thickness side; on a slope
    // each corner's cross position follows the band there.
    private corners() {
        const len = this.length.get();

        const aLeft = -this.outset(this.terminationA, "left");
        const aRight = -this.outset(this.terminationA, "right");
        const bLeft = len + this.outset(this.terminationB, "left");
        const bRight = len + this.outset(this.terminationB, "right");

        const corner = (along: number, side: "left" | "right") => {
            const cross = this.facesAt(along)[side];
            return this.orientation === "vertical" ? {x: cross, y: along} : {x: along, y: cross};
        };

        return {
            aLeft: corner(aLeft, "left"),
            aRight: corner(aRight, "right"),
            bLeft: corner(bLeft, "left"),
            bRight: corner(bRight, "right"),
        };
    }

    // the two corners of one end in local (along, cross) coords, with the outsets
    // applied: the end cut runs from `left` to `right`.
    endCut(end: "A" | "B"): {left: {along: number; cross: number}; right: {along: number; cross: number}} {
        const len = this.length.get();
        const term = end === "A" ? this.terminationA : this.terminationB;
        const along = (corner: "left" | "right") => end === "A" ? -this.outset(term, corner) : len + this.outset(term, corner);
        const at = (corner: "left" | "right") => {
            const a = along(corner);
            return {along: a, cross: this.facesAt(a)[corner]};
        };
        return {left: at("left"), right: at("right")};
    }

    // the length of the band along its slope per unit of the length axis (1 when
    // straight).
    slopeScale(): number {
        const len = this.length.get();
        if(len <= 0) return 1;
        const d = this.slope.b - this.slope.a;
        return Math.sqrt(len * len + d * d) / len;
    }

    // the thickness dimension is shown when this stick is selected or when the
    // "Show All Dimensions" view toggle is on.
    private syncThicknessDimensionVisible() {
        this.thicknessDimension?.setVisible(this.selected || this.elev.viewSettings.dimShowAll.value);
    }

    // lazily build the cutdown callout the first time an undercut is drawn, tracked so
    // it's torn down with the stick. Centered under the jamb, red to match the cut line.
    private ensureUndercutLabel(): Label {
        if(this.undercutLabel) return this.undercutLabel;
        this.undercutLabel = new Label({text: "", color: lineRed, fontSize: CUTDOWN_LABEL_FONT, anchorX: 0.5, anchorY: 0});
        this.add(this.undercutLabel);
        return this.undercutLabel;
    }

    draw() {
        const c = this.corners();

        // selected state takes precedence over hover, which takes precedence
        // over the default styling.
        const fillColor = this.selected ? FILL_SELECTED : this.hovered ? FILL_HOVER : FILL_DEFAULT;
        const fillAlpha = this.selected ? 0.45 : this.hovered ? 0.35 : 0.2;

        const g = this.lines;
        g.clear();

        const outline = [c.aLeft, c.bLeft, c.bRight, c.aRight];

        if(this.airGap) {
            this.drawAirGap(g, outline, fillColor, fillAlpha);
        } else {
            g.lineStyle(2, lineBlueHeavy, 1);
            g.beginFill(fillColor, fillAlpha);

            // trace the outline: A-left -> B-left -> B-right -> A-right
            g.drawPolygon(outline.flatMap(p => [p.x, p.y]));

            g.endFill();
        }

        // frame undercut (vertical jambs only). Two very different meanings by view:
        //   - Locations (undercutHatch): the jamb is drawn at full nominalHeight, and
        //     the undercut is the bottom `undercut` band being cut off — a red hatch
        //     over [len-u, len], with the remaining steel above it.
        //   - Grid (plain line): the frame is *physically shortened* by the undercut
        //     already (the door cell height subtracts it — see MdFrameSpec), so there's
        //     no band to hatch. We just mark the cut with a red line at the stick's real
        //     bottom edge. That edge is the B-end, which the bottom jamb extends past
        //     `len` by the meeting band's thickness (extend termination) — so draw it at
        //     the B-end corners, not at `len`, or the line floats a band above the base.
        let showCutdownLabel = false;
        if(this.undercut && this.orientation === "vertical") {
            const len = this.length.get();
            const u = Math.min(this.undercut.get(), len);
            if(u > 0) {
                if(this.undercutHatch) {
                    drawCutMarker(g, 0, len - u, this.thickness.get(), u);
                } else {
                    // the frame is physically shortened, so there's no band to show the
                    // amount — mark the cut with a red line at the real bottom edge.
                    g.lineStyle(1, lineRed, 1);
                    g.moveTo(c.bLeft.x, c.bLeft.y);
                    g.lineTo(c.bRight.x, c.bRight.y);

                    // hang a "Cutdown: {dim}" callout below it, but only on the designated
                    // jamb and only on the printout (external dimensions) — the interactive
                    // editor stays uncluttered.
                    if(this.showCutdown && this.elev.viewSettings.externalDimensions.value) {
                        const label = this.ensureUndercutLabel();
                        label.setText("Cutdown: " + this.elev.draw.formatDimension(u));
                        const midX = (c.bLeft.x + c.bRight.x) / 2;
                        const bottomY = Math.max(c.bLeft.y, c.bRight.y);
                        label.graphic.position.set(midX, bottomY + CUTDOWN_LABEL_GAP);
                        showCutdownLabel = true;
                    }
                }
            }
        }
        if(this.undercutLabel) this.undercutLabel.graphic.visible = showCutdownLabel;

        // the length may have changed, so re-centre the cut-plan mark.
        this.drawCutLabel();

        // geometry may have changed (resize/miter); keep the hit area in sync.
        this.refreshHitArea();

        // the thickness dimension reads our thickness/length off the same FrmVars,
        // so redraw it here — otherwise an edit (or a layout that changes our
        // length) leaves its label and line span stale. Respects its own
        // visibility flag (driven by the "Show All Dimensions" toggle). Absent on
        // an inert stick.
        this.thicknessDimension?.draw();
    }

    // an air gap is open space: on the printout it draws nothing (the stacked dimensions
    // still measure it); on screen a dashed outline marks it so it can be found, filled
    // only while hovered or selected.
    private drawAirGap(g: Graphics, outline: {x: number; y: number}[], fillColor: number, fillAlpha: number) {
        if(this.elev.viewSettings.externalDimensions.value) return;

        if(this.selected || this.hovered) {
            g.lineStyle(0);
            g.beginFill(fillColor, fillAlpha);
            g.drawPolygon(outline.flatMap(p => [p.x, p.y]));
            g.endFill();
        }

        g.lineStyle(1.5, lineBlueHeavy, AIR_GAP_LINE_ALPHA);
        drawDashedPolygon(g, outline);
    }
}

function sameTermination(a: StickTermination, b: StickTermination): boolean {
    if(a.kind !== b.kind) return false;
    if(!!a.corners !== !!b.corners) return false;
    if(a.corners && b.corners && (a.corners.left !== b.corners.left || a.corners.right !== b.corners.right)) return false;
    if(a.kind === "cap" || b.kind === "cap") return true;
    if(a.otherThickness.get() !== b.otherThickness.get()) return false;
    if(a.kind === "miter" && b.kind === "miter") return a.direction === b.direction;
    return true;
}
